import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DailyService } from '@app/core';
import { AlertService } from '@app/core/services';
import { Resp } from '@app/core/services/api.service';
import { AttachmentService } from '@app/core/services/attachment.service';
import { SHARED_MODULES } from '@app/shared';
import { TextEditorComponent } from '@app/shared/modules/text-editor/text-editor.component';
import { addDays, format, max, parse, startOfDay } from 'date-fns';
import { isEmpty, isNil, isObject } from 'lodash';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { AttachmentDropzoneComponent, ExistingAttachment } from '../attachment-dropzone/attachment-dropzone.component';
import { UploadFile } from '../attachment-dropzone/dropzone.types';
import { DAILY_TEXT_EDITOR_URL, DAILY_TOOLBAR, DAILY_UPLOAD, FormBase, ModalDailyTab, SidenavDailyShow } from './base';

@Component({
  imports: [SHARED_MODULES, TextEditorComponent, AttachmentDropzoneComponent],
  templateUrl: './form-daily.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormDailyComponent extends FormBase implements OnDestroy {
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _cdref = inject(ChangeDetectorRef);
  private readonly _dailyService = inject(DailyService);
  private readonly _alertService = inject(AlertService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _destroyRef = inject(DestroyRef);

  public override title = '';
  public override titleElement = '';
  public override targetsLabel = 'eclass.aula.cadastrar-para';
  public override publishLabel = 'eclass.aula.publicar';
  public override submitted$: Observable<boolean>;
  public override type = ModalDailyTab.Daily;
  public override form: FormGroup;
  public override showSidenavEnd = false;
  public override showSidenavDaily = true;
  public override sidenavEndData: any;
  public readonly textEditorUrl = DAILY_TEXT_EDITOR_URL;
  public readonly toolbar = DAILY_TOOLBAR;
  public config: DropzoneConfigInterface = { ...DAILY_UPLOAD, params: { folder: 'dailies' } };
  public date$ = new BehaviorSubject<Date>(undefined);
  public pinnedCtrl = new FormControl(false);
  public isLegacy = signal(false);
  private _submitted$ = new BehaviorSubject(false);

  constructor() {
    super();
    this.date$.next(startOfDay(new Date()));
    this.submitted$ = this._submitted$.asObservable();
    this.initForm();

    this.pinnedCtrl.valueChanges.pipe(takeUntilDestroyed(this._destroyRef)).subscribe((value) => {
      if (value) {
        const date = this.date$.value ?? startOfDay(new Date());
        this.form.get('date_pin').setValue(addDays(date, 15));
      } else {
        this.form.get('date_pin').reset();
      }
    });
  }

  public get existingAttachments(): ExistingAttachment[] {
    const attachments = this.attachments.value ?? [];

    return attachments
      .filter((att: any) => att.id !== null)
      .map((att: any) => ({
        id: att.id,
        name: att.title ?? att.file,
        size: att.size ?? 0,
        url: att.url,
        mimeType: att.mime_type,
      }));
  }

  private get attachments(): FormControl {
    return this.form.get('attachments') as FormControl;
  }

  public ngOnDestroy() {
    this._submitted$.complete();
  }

  public override set targets(value: SidenavDailyShow[]) {
    if (value.length) {
      const maxDate = max(value.map((daily) => daily.date));
      if (!!this.pinnedCtrl.value && maxDate > this.form.value.date_pin) {
        this.form.get('date_pin').setValue(addDays(maxDate, 15));
      }
      this.date$.next(maxDate);
    }
  }

  public override setLegacy(value: boolean) {
    this.isLegacy.set(value);
  }

  public onFileRemoved(targetId: string) {
    const targetFile = this.attachments.value.find(
      (att: any) => String(att.id) === targetId || att.dropzoneId === targetId,
    );

    const updateForm = () => {
      const files = this.attachments.value.filter(
        (att: any) => String(att.id) !== targetId && att.dropzoneId !== targetId,
      );
      this.attachments.setValue(files);
    };

    if (targetFile?.id) {
      this._attachmentService.deleteAttachment(targetFile.id).subscribe((res) => {
        if (res.ret) {
          this._alertService.success({ message: 'geral.excluido-sucesso' });
        } else {
          this._alertService.alert({ message: res.msg || 'geral.excluido-erro' });
        }
        updateForm();
      });
    } else {
      updateForm();
    }
  }

  public onUploaded(event: UploadFile) {
    const file = {
      id: null,
      dropzoneId: event.id,
      file: event.uploadedFile,
      title: event.name,
      size: event.size,
      extension: event.name.split('.').pop()?.toLowerCase(),
      key: event.key,
      url: null,
    };
    this.attachments.setValue([...(this.attachments.value || []), file]);
    this.attachments.markAsDirty();
    this.attachments.markAsTouched();
  }

  public onFilesChanged(files: UploadFile[]) {
    const isUploading = files.some((f) => f.status === 'uploading' || f.status === 'waiting');
    this._submitted$.next(isUploading);
  }

  public override markAllAsTouched() {
    this.form.markAllAsTouched();
    this._cdref.markForCheck();
  }

  public override patchValue(data: any) {
    this.form.patchValue({
      ...data,
      date_pin: data.date_pin ? parse(data.date_pin, 'yyyy-MM-dd', new Date()) : null,
      learningTrailsElements: data.trail_elements,
      games: data.games ? data.games.map((g) => g.id) : [],
    });

    if (!!data.date_pin) {
      this.pinnedCtrl.setValue(true);
    }
  }

  public override resetForm() {
    this.form.reset();
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this._cdref.markForCheck();
  }

  public override onSave(params: any): Observable<boolean> {
    return this._alertService
      .confirm({ message: 'eclass.aula.salvar' })
      .pipe(switchMap((confirm) => (confirm ? this.save(params) : of(false))));
  }

  private save(params: any): Observable<boolean> {
    const targets = params.targets.map((target: SidenavDailyShow) => ({
      id: target.id,
      entity_id: target.entity.id,
      group_id: target.group.id,
      subject_id: target.subject.id,
      date: format(target.date, 'yyyy-MM-dd'),
    }));
    const data = {
      ...params.form,
      date_pin: !!params.form.date_pin ? format(params.form.date_pin, 'yyyy-MM-dd') : null,
      published: params.publish,
    };
    const store$ = this.store(targets, data);
    const update$ = this.update(targets, data);
    return combineLatest([store$, update$]).pipe(
      take(1),
      map(([store, update]) => store && update),
      tap((result) => !!result && this._alertService.success({ message: 'geral.salvo-com-sucesso' })),
    );
  }

  private store(params: SidenavDailyShow[], data: any): Observable<boolean> {
    const target = params.filter(({ id }) => isNil(id)).map(({ id, ...target }) => target);
    if (!isEmpty(target)) {
      return this.handleResponse(
        this._dailyService.store({ ...data, attachments: data?.attachments?.map(({ id, ...a }) => a), target }),
      );
    }
    return of(true);
  }

  private update(params: SidenavDailyShow[], data: any): Observable<boolean> {
    const target = params.find(({ id }) => !isNil(id));
    if (!!target) {
      return this.handleResponse(this._dailyService.update(target.id, { ...data, ...target }));
    }
    return of(true);
  }

  private handleResponse(observable$: Observable<Resp<any>>): Observable<boolean> {
    return observable$.pipe(
      catchError((data) => {
        const message = data.msg || data.error?.msg;
        return of({ ret: 0, data: isObject(message) ? JSON.stringify(message) : message });
      }),
      switchMap((result) => {
        if (!result.ret) {
          const message = !!result.data ? { innerHtml: result.data } : { message: 'geral.erro-ao-salvar' };
          return this._alertService.alert(message).pipe(map(() => false));
        }
        return of(true);
      }),
    );
  }

  private initForm() {
    this.form = this._formBuilder.group({
      title: [null, [Validators.required, Validators.maxLength(100)]],
      date_pin: null,
      content: null,
      attachments: [[]],
      learningTrailsElements: [[]],
      games: [[]],
    });
  }
}
